import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getQuestionDetail,
  patchEditQuestion,
} from '../../../services/MyQuestions/QuestionService';
import QuestionCard from '../CreateQuestion/Questions/QuestionCard';
import {
  getObjectives,
  getPurposes,
} from '../../../services/questionService';
import questionReducer, {
  cardHandlers,
  getQuestionWarnings,
  getSaveErrorMessage,
  incompleteByIndex,
  missingErrors,
  missingFields,
  scrollToFirstInvalid,
  validateQuestions,
} from '../CreateQuestion/Questions/QuestionUtils';
import {
  EditUnavailable,
  NotSavedNotice,
  PrintedQuestion,
  SAVE_REFUSALS,
  StaleNotice,
  bookletRefusal,
  editBlockReason,
  loadFailure,
  saveRefusalMessage,
  useEditBackLinks,
} from '../CreateQuestion/Questions/EditNotices';
import FormHeader from '../CreateQuestion/FormHeader';
import { Button } from '../../../components/forms/Buttons/Button';
import { toast } from 'react-toastify';
import SimpleLoader from '../../../components/Loader/SimpleLoader';
import PassagePanel from '../../../components/Passages/PassagePanel';
import { UserContext } from '../../../utils/contexts/UserContext';
import { isSubjectFolder } from '../../../utils/folders';
import { getApiErrorCode } from '../../../utils/apiError';
import { BookletLinks, questionBooklets } from '../../QuestionPage/review/bookletLock';
import { changedFields, editsToRestore } from './staleEdits';

const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);

// The question as the form edits it: the pickers take uuids (and the names to show).
const formQuestion = (detail) => {
  // subtopic.topic is {uuid, name} on the current API (a bare uuid on older backends).
  const topic = detail?.subtopic?.topic;
  return {
    ...detail,
    id: 0,
    topic: topic?.uuid ?? topic,
    topic_name: topic?.name,
    subtopic_name: detail?.subtopic?.name,
    objective: detail?.objective?.uuid,
    subtopic: detail?.subtopic?.uuid,
    purpose: detail?.purpose?.uuid,
    answer_keys: detail?.answer_keys || [],
  };
};

// What an edit sends: the question's own fields (its folder and subject move elsewhere, a passage links it
// itself), and base_version, the version the form was opened on: a question someone else changed since
// answers 409 stale_version. A backend without versions gets none.
const editBody = (question, baseVersion) => {
  const { name, body, difficulty, subtopic, purpose, objective, answer_keys } = question;
  return {
    name,
    body,
    difficulty,
    subtopic,
    purpose,
    objective,
    answer_keys,
    ...(baseVersion !== undefined ? { base_version: baseVersion } : {}),
  };
};

export default function EditQuestion() {
  const [t] = useTranslation();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const navState = useLocation().state;
  const from = navState?.from;
  // Opened with the question page's Edit button: go back to that page after saving (it reloads the question).
  const fromQuestionPage = Boolean(from);
  const { user } = useContext(UserContext) || {};
  const backLinks = useEditBackLinks(uuid);
  const [purposes, setPurposes] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [questions, dispatch] = useReducer(questionReducer, []);
  // The question as loaded: its status, folder, author, `actions` (what the user may do) and `version`.
  const [detail, setDetail] = useState(null);
  const [load, setLoad] = useState({ loading: true, error: null });
  const [attempt, setAttempt] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // The version the form edits, sent as base_version (undefined: a backend without versions).
  const [baseVersion, setBaseVersion] = useState(undefined);
  // The required fields the question already lacked when the form opened: an edit may leave them empty.
  const [initialMissing, setInitialMissing] = useState([]);
  // QuestionCard keeps the body and pickers itself: a reloaded question mounts it anew.
  const [formKey, setFormKey] = useState(0);
  // After a 409 stale_version: {edits}, the user's changes the reload replaced (null: none); `draftSent` when the
  // draft editor sent the user here, its draft sent to review meanwhile (from another tab); {restored} once the
  // changes are back in the form.
  const [stale, setStale] = useState(() => navState?.stale || null);
  // A save refused for the question's stage (403 edit_locked / draft_not_own): no more saves from this form.
  const [refusal, setRefusal] = useState(null);
  // A save refused because an active BTS booklet prints the question (409 question_in_booklet): {message, booklets,
  // key}. Said above the form, which keeps the user's changes; a later save is sent again (the booklet may be back in
  // draft by then). `booklets`: their links, for an admin (who returns them to draft); `key` brings the notice into
  // view again on each refusal.
  const [bookletLock, setBookletLock] = useState(null);

  const inSubject = isSubjectFolder(detail?.folder);

  const applyDetail = useCallback((res) => {
    const question = formQuestion(res);
    dispatch({ type: 'RESET', questions: [question] });
    setDetail(res);
    setBaseVersion(res && 'version' in res ? res.version : undefined);
    setInitialMissing(isSubjectFolder(res?.folder) ? missingFields(question) : []);
    setErrors({});
    setFormKey((key) => key + 1);
  }, []);

  // Loaded again for `from`, not for the whole navigation state: forgetting the draft editor's report (below)
  // loads nothing.
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setLoad({ loading: true, error: null });
    Promise.all([
      getPurposes({ params: {}, signal }).catch(() => []),
      getObjectives({ params: {}, signal }).catch(() => []),
      getQuestionDetail(uuid, { signal }),
    ])
      .then(([purposesRes, objectivesRes, questionRes]) => {
        if (signal.aborted) return;
        // A draft is written in the draft editor: a save here would submit its stored (empty) question.
        if (questionRes?.status?.code === 'DRAFT') {
          navigate(`/my-questions/draft/${uuid}`, { replace: true, state: from ? { from } : null });
          return;
        }
        setPurposes(listOf(purposesRes));
        setObjectives(listOf(objectivesRes));
        applyDetail(questionRes);
        setLoad({ loading: false, error: null });
      })
      .catch((error) => {
        if (!signal.aborted) setLoad({ loading: false, error });
      });

    return () => {
      controller.abort();
    };
  }, [uuid, attempt, applyDetail, navigate, from]);

  // The draft editor's report is shown once: history.state survives a reload and Back.
  useEffect(() => {
    if (!navState?.stale) return;
    const { stale: shown, ...rest } = navState;
    navigate(`/my-questions/edit/${uuid}`, { replace: true, state: rest });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // After a 409 stale_version: the question as it is now replaces the user's changes, which are kept to be put
  // back (`edits`).
  const reloadQuestion = (edits) => {
    setLoad({ loading: true, error: null });
    getQuestionDetail(uuid)
      .then((res) => {
        applyDetail(res);
        setStale({ edits });
        setLoad({ loading: false, error: null });
      })
      .catch((error) => setLoad({ loading: false, error }));
  };

  // "Restore my changes": the user's changes a 409 replaced, back in the form (the question loaded stays the base).
  const restoreEdits = (edits) => {
    dispatch({ type: 'RESET', questions: [{ ...questions[0], ...edits }] });
    setErrors({});
    setFormKey((key) => key + 1);
    setStale({ restored: true });
  };

  const save = (e) => {
    if (e.detail > 1) return;
    // In an exam's subject what review needs may not be emptied; the gaps the question already had may stay.
    const validation = validateQuestions(questions, { inSubject, allowMissing: initialMissing });
    setErrors(validation);
    if (validation[0]) {
      toast.error(t('my_questions.validation.error'));
      scrollToFirstInvalid();
      return;
    }
    const warns = getQuestionWarnings(questions, { inSubject });
    warns.forEach((w) => toast.warn(w));
    setIsSaving(true);
    patchEditQuestion(uuid, editBody(questions[0], baseVersion))
      .then((res) => {
        // The version a further save would start from (the page leaves now).
        if (res && 'version' in res) setBaseVersion(res.version);
        toast.success(t('my_questions.toasts.question_edited'));
        navigate(fromQuestionPage ? -1 : '/my-questions');
      })
      .catch((err) => {
        const code = getApiErrorCode(err);
        const incomplete = incompleteByIndex(err);
        if (incomplete) {
          // An author's edit of a question waiting for review emptied what review needs.
          setErrors({ 0: missingErrors(incomplete[0], questions[0]) });
          toast.error(t('errors.incomplete'));
          scrollToFirstInvalid();
        } else if (code === 'stale_version') {
          reloadQuestion(changedFields(formQuestion(detail), questions[0]));
        } else if (SAVE_REFUSALS.includes(code)) {
          setRefusal(code);
          toast.error(saveRefusalMessage(t, code));
        } else if (code === 'question_in_booklet') {
          setBookletLock((prev) => ({ ...bookletRefusal(t, err, user, 'question'), key: (prev?.key || 0) + 1 }));
        } else {
          toast.error(getSaveErrorMessage(t, err));
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  if (load.loading) return <SimpleLoader className="h-10" />;

  if (load.error) {
    const failure = loadFailure(t, load.error);
    return (
      <EditUnavailable
        title={failure.title}
        message={failure.message}
        links={backLinks}
        onRetry={failure.retry ? () => setAttempt((n) => n + 1) : undefined}
      />
    );
  }

  // An active BTS booklet prints the question: every change is refused (its actions.edit is false too), so the page
  // says why up front instead of a form whose save could only fail.
  const printedBy = questionBooklets(detail);
  if (printedBy.length) {
    return <PrintedQuestion question={detail} booklets={printedBy} links={backLinks} />;
  }

  // The route itself is guarded: a question the user may not edit now (actions.edit false) opens no form.
  // Older backends send no actions; the save is refused there if need be.
  if (detail?.actions && detail.actions.edit === false) {
    return (
      <EditUnavailable
        locked
        title={t('my_questions.edit_guard.blocked_title')}
        message={editBlockReason(t, detail, user)}
        question={detail}
        links={backLinks}
      />
    );
  }

  const question = questions[0];
  // Still lacking what it lacked on opening: said, not refused.
  const reviewNeeds = inSubject && question ? missingFields(question).filter((field) => initialMissing.includes(field)) : [];
  const restorable = editsToRestore(stale?.edits, question);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 motion-safe:animate-fade-in">
      <FormHeader title={t('my_questions.editor.edit_heading')} mode="edit" inSubject={inSubject} linked={!!question?.passage} />
      {stale && (
        <StaleNotice
          stale={stale}
          onRestore={restorable ? () => restoreEdits(restorable) : undefined}
          onClose={() => setStale(null)}
        />
      )}
      {refusal && <NotSavedNotice message={saveRefusalMessage(t, refusal)} links={backLinks} />}
      {!refusal && bookletLock && (
        <NotSavedNotice
          key={`booklet-${bookletLock.key}`}
          message={
            <>
              {bookletLock.message}
              <BookletLinks booklets={bookletLock.booklets} />
            </>
          }
          links={backLinks}
        />
      )}
      {/* The passage the question is based on: shown to write against, not edited here. */}
      <PassagePanel passage={question?.passage} />
      {question && (
        <QuestionCard
          key={formKey}
          isEdit
          required={inSubject}
          reviewNeeds={reviewNeeds}
          subject={detail?.subject?.uuid}
          errors={errors[0]}
          question={question}
          purposes={purposes}
          objectives={objectives}
          {...cardHandlers(0, question, dispatch, setErrors)}
        />
      )}
      {/* One main action; phones give it the whole width (44px). */}
      <div className="flex justify-end pb-6">
        <Button
          className="max-sm:h-11 max-sm:w-full"
          color="primary"
          text={t('my_questions.actions.save_and_exit')}
          disabled={isSaving || !!refusal}
          loading={isSaving}
          onClick={save}
        />
      </div>
    </div>
  );
}
